/**
 * Footer modal handlers — FAQ, Mitä uutta (changelog) and Lähetä palautetta
 * (feedback). The first two are static i18n bodies; the feedback form posts
 * to the existing public endpoint `POST /api/v1/public/contact` (set up in
 * M0 alongside the landing page).
 */
import { APIRequestError } from '../api/client.ts';
import { submitFeedback } from '../api/feedback.ts';
import { FAQ_DATA } from '../data/faq.ts';
import { t, applyToDOM, getLang } from '../i18n/index.ts';
import { currentUser } from '../state/session.ts';

// Currently selected FAQ category. Persists for the session so re-opening the
// modal returns to the last category the user was reading.
let faqCurrentCat = FAQ_DATA[0].id;

function show(id: string): void {
  document.getElementById(id)!.style.display = 'flex';
}
function hide(id: string): void {
  document.getElementById(id)!.style.display = 'none';
}

export function bindFooterModals(): void {
  document.getElementById('footerFaq')!.addEventListener('click', () => {
    renderFaq();
    applyToDOM(document.getElementById('faqModal')!);
    show('faqModal');
  });
  document.getElementById('footerChangelog')!.addEventListener('click', () => {
    document.getElementById('changelogBody')!.innerHTML = t('changelog_body');
    applyToDOM(document.getElementById('changelogModal')!);
    show('changelogModal');
  });
  document.getElementById('footerFeedback')!.addEventListener('click', () => {
    // Pre-fill name/email from session if available — saves typing for
    // logged-in users and avoids them sending feedback as anonymous.
    const u = currentUser();
    const nameEl = document.getElementById('feedbackName') as HTMLInputElement;
    const emailEl = document.getElementById('feedbackEmail') as HTMLInputElement;
    if (u) {
      if (!nameEl.value) nameEl.value = u.name || '';
      if (!emailEl.value) emailEl.value = u.email;
    }
    show('feedbackModal');
  });

  document.getElementById('faqCloseBtn')!.addEventListener('click', () => hide('faqModal'));
  document
    .getElementById('changelogCloseBtn')!
    .addEventListener('click', () => hide('changelogModal'));
  document
    .getElementById('feedbackCloseBtn')!
    .addEventListener('click', () => hide('feedbackModal'));

  // Close on backdrop click.
  for (const id of ['faqModal', 'changelogModal', 'feedbackModal']) {
    const overlay = document.getElementById(id)!;
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) hide(id);
    });
  }

  bindFeedbackForm();
}

// Full categorised FAQ port from the reference mockup (FAQ_DATA). Renders a
// category nav + an accordion of Q&A for the selected category into #faqBody.
// Answer HTML is trusted, hand-authored content from src/data/faq.ts; question
// text is escaped defensively.
function renderFaq(): void {
  const body = document.getElementById('faqBody');
  if (!body) return;
  const isEn = getLang() === 'en';

  if (!FAQ_DATA.some((c) => c.id === faqCurrentCat)) faqCurrentCat = FAQ_DATA[0].id;
  const cat = FAQ_DATA.find((c) => c.id === faqCurrentCat) ?? FAQ_DATA[0];

  const cats = FAQ_DATA.map((c) => {
    const label = isEn ? c.title_en : c.title_fi;
    const active = c.id === faqCurrentCat ? ' active' : '';
    return `<button type="button" class="faq-cat-btn${active}" data-cat="${c.id}">${escapeHtml(label)}</button>`;
  }).join('');

  const items = cat.items
    .map((item, idx) => {
      const q = isEn ? item.q_en : item.q_fi;
      const a = isEn ? item.a_en : item.a_fi;
      const openClass = idx === 0 ? ' open' : '';
      return `
        <div class="faq-item${openClass}">
          <div class="faq-q" role="button" tabindex="0">
            <span class="faq-q-icon" aria-hidden="true">+</span>
            <span>${escapeHtml(q)}</span>
          </div>
          <div class="faq-a">${a}</div>
        </div>`;
    })
    .join('');

  body.innerHTML = `
    <div class="faq-categories">${cats}</div>
    <div class="faq-content">
      <div class="faq-cat-title">${escapeHtml(isEn ? cat.title_en : cat.title_fi)}</div>
      <div class="faq-cat-intro">${escapeHtml(isEn ? cat.intro_en : cat.intro_fi)}</div>
      ${items}
    </div>`;

  // Category switching.
  body.querySelectorAll<HTMLButtonElement>('.faq-cat-btn').forEach((btn) => {
    btn.addEventListener('click', () => {
      faqCurrentCat = btn.dataset.cat || FAQ_DATA[0].id;
      renderFaq();
    });
  });

  // Accordion toggle (click + keyboard).
  body.querySelectorAll<HTMLDivElement>('.faq-q').forEach((q) => {
    const toggle = (): void => {
      q.closest('.faq-item')?.classList.toggle('open');
    };
    q.addEventListener('click', toggle);
    q.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        toggle();
      }
    });
  });
}

function escapeHtml(s: string): string {
  return s.replace(
    /[&<>"']/g,
    (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!,
  );
}

function bindFeedbackForm(): void {
  const form = document.getElementById('feedbackForm') as HTMLFormElement;
  const nameEl = document.getElementById('feedbackName') as HTMLInputElement;
  const emailEl = document.getElementById('feedbackEmail') as HTMLInputElement;
  const msgEl = document.getElementById('feedbackMessage') as HTMLTextAreaElement;
  const status = document.getElementById('feedbackMsg')!;
  const submit = document.getElementById('feedbackSubmit') as HTMLButtonElement;

  form.addEventListener('submit', async (ev) => {
    ev.preventDefault();
    status.style.display = 'none';
    status.classList.remove('ok', 'err');

    const message = msgEl.value.trim();
    if (!message) {
      status.textContent = t('feedback_err_empty');
      status.classList.add('err');
      status.style.display = 'block';
      return;
    }

    submit.disabled = true;
    const submitOriginal = submit.textContent;
    submit.textContent = t('feedback_submitting');

    try {
      // M5: route through the new /api/v1/smt/feedback endpoint so the
      // submission gets persisted to `smt_feedback` (admin queue) instead
      // of only emailing the SMTP recipient. The client context (URL +
      // view) is captured so admins can reproduce the issue.
      await submitFeedback({
        name: nameEl.value.trim(),
        email: emailEl.value.trim(),
        message,
        lang: getLang(),
        context: JSON.stringify({
          url: window.location.href,
          hash: window.location.hash,
        }),
      });
      status.textContent = t('feedback_thanks');
      status.classList.add('ok');
      status.style.display = 'block';
      msgEl.value = '';
      // Auto-close after 2 seconds.
      setTimeout(() => hide('feedbackModal'), 2000);
    } catch (e) {
      const detail = e instanceof APIRequestError ? e.body.error || e.message : (e as Error).message;
      status.textContent = `${t('feedback_err_send')} (${detail})`;
      status.classList.add('err');
      status.style.display = 'block';
    } finally {
      submit.disabled = false;
      submit.textContent = submitOriginal;
    }
  });
}
